import Income, {getIncomeTypeDef, IncomeType} from "../../../model/Income";
import {Box, Grid, IconButton, InputAdornment, Paper, Typography, useTheme} from "@mui/material";
import * as React from "react";
import {ReactNode, useEffect, useState} from "react";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {
  CreateAssetInput,
  CreateDeductionInput,
  CreateIncomeInput,
  CreateTaxInput,
  UpdateDeductionInput,
  UpdateIncomeInput,
  UpdateTaxInput
} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";
import Deduction, {DeductionType} from "../../../model/Deduction";
import Model from "../../../model/Model";
import Schedule, {Frequency} from "../../../model/Schedule";
import ScheduleSelector from "../../../components/controls/ScheduleSelector";
import FormGroupSpacer from "../../../components/form/FormGroupSpacer";
import MilestoneDateFieldValidator from "../../../components/form/MilestoneDateFieldValidator";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import {
  createUUID,
  getISODateFromDate,
  getISODateToday,
  humanizeString,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import {isNumber} from "../../../model/ModelUtilities";
import Milestone, {MilestoneId} from "../../../model/Milestone";
import {addYears} from "date-fns";
import Person from "../../../model/Person";
import Tracking from "../../../components/Tracking";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Snapshot from "../../../model/Snapshot";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import FormValidator from "../../../components/form/FormValidator";
import DialogButton from "../../../components/form/DialogButton";
import Tax, {TaxType} from "../../../model/Tax";
import Asset, {
  AssetCategory,
  AssetType,
  AssetTypeDef,
  AssetTypeDefs,
  listAssetTypeDefs,
  Risk
} from "../../../model/Asset";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import ModelStore from "../../../stores/ModelStore";
import {AddCircleRounded} from "@mui/icons-material";
import BasicMenuButton from "../../../components/controls/BasicMenuButton";
import {taxMenuOptions} from "./BudgetView";
import IModelItem from "../../../model/IModelItem";
import useIsMobile from "../../../components/useIsMobile";

type IncomeEdit = {
  id: string
  createdAt?: string
  description: string
  incomeType: IncomeType
  amount: string,
  imputed?: boolean
  locked?: boolean,
}

type DeductionEdit = {
  id: string
  createdAt?: string
  description: string
  asset?: Asset
  amount: string
  deductionType: DeductionType
  assetRequired?: boolean
}

type TaxEdit = {
  id: string
  createdAt?: string
  description: string
  taxType: TaxType
  amount: string
}

export const incomeOptions = [
  {id: "", description: "Cash Awards, Benefits or Bonuses", amount: "$0", incomeType: IncomeType.WagesSalariesTips},
  {id: "", description: "Housing Allowance", amount: "$0", incomeType: IncomeType.WagesSalariesTips},
  {id: "", description: "Paid Time Off Payout", amount: "$0", incomeType: IncomeType.WagesSalariesTips},
  {id: "", description: "Tips", amount: "$0", incomeType: IncomeType.WagesSalariesTips},
  {id: "", description: "Other Wages", amount: "$0", incomeType: IncomeType.WagesSalariesTips},
]

export const imputedIncomeOptions = [
  {id: "", description: "Adoption Assistance", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Educational Assistance or Tuition Benefits", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Employer Paid Disability Insurance", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Employer Provided Housing or Meals", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Employer Provided Life Ins", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Gym membership", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Personal Use of Company Car", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Relocation Expense", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
  {id: "", description: "Other Non-Cash Benefits", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
]

const beforeTaxIncomeDesc = "Before Tax Income"

type SortOrder = {
  asset: number
  income: number
  deduction: number
  tax: number
}

const PaycheckEditDialog = ({
  open,
  income,
  model,
  snapshot,
  onClose,
  onSave,
  onDelete,
  onArchive,
  quickForm
}: {
  open?: boolean
  income: Income
  model: Model
  snapshot?: Snapshot
  onClose?(): void
  onSave?(income: Income): void
  onDelete?(income: Income): void
  onArchive?(income: Income): void
  quickForm?: boolean
}) => {
  const [incomeModel, setIncomeModel] = useState<Income>(income)
  const [description, setDescription] = useState<string>("")
  const [amount, setAmount] = useState<string | undefined>()
  const [amountDate, setAmountDate] = useState<Date | undefined>()
  const [amountLock, setAmountLock] = useState<boolean>(false)
  const [incomeType, setIncomeType] = useState<IncomeType | undefined>()
  const [person, setPerson] = useState<Person | undefined>()
  const [owner, setOwner] = useState<string | undefined>()
  const [allowJoint, setAllowJoint] = useState<boolean>(true)
  const [schedule, setSchedule] = useState<Schedule | undefined>()
  const [start, setStart] = useState<string>()
  const [startLock, setStartLock] = useState<boolean>(false)
  const [end, setEnd] = useState<string>()
  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [annualInfLock, setAnnualInfLock] = useState<"Model" | "User" | undefined>()
  const [survivorPercent, setSurvivorPercent] = useState<string | undefined>()
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [archived, setArchived] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isPaycheck, setIsPaycheck] = useState<boolean>(false)
  const [incomes, setIncomes] = useState<IncomeEdit[]>([])
  const [deductions, setDeductions] = useState<DeductionEdit[]>([])
  const [taxes, setTaxes] = useState<TaxEdit[]>([])
  const [newAssets, setNewAssets] = useState<Asset[]>([])
  const [incomesTotal, setIncomesTotal] = useState<number>(0)
  const [imputedIncomesTotal, setImputedIncomesTotal] = useState<number>(0)
  const [beforeTaxDeductionsTotal, setBeforeTaxDeductionsTotal] = useState<number>(0)
  const [taxableIncomeTotal, setTaxableIncomeTotal] = useState<number>(0)
  const [taxesTotal, setTaxesTotal] = useState<number>(0)
  const [afterTaxDeductionsTotal, setAfterTaxDeductionsTotal] = useState<number>(0)
  const [employerContributionsTotal, setEmployerContributionsTotal] = useState<number>(0)
  const [netIncomeTotal, setNetIncomeTotal] = useState<number>(0)
  const { modelStore, userStore, calculator, notify } = useResources()

  const isMobile = useIsMobile()
  const theme = useTheme()

  const styles = {
    instructions: {
      fontSize: 14
    },
    summaryHeading: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      fontSize: 16,
      fontWeight: 600,
      borderBottom: "1px solid #ccc",
    },
    summaryCell: {
      color: theme.palette.text.primary,
      borderBottom: "1px solid #ccc",
      paddingRight: 1,
      paddingTop: 0
    },
    netCell: {
      color: theme.palette.text.primary,
      borderBottom: "1px solid #000",
      paddingRight: 1,
      paddingTop: 0
    },
    amount: {
      minWidth: "80px"
    },
    dialogContent: {
      mt:2,
      mb:2,
      px:0,
      pb:0,
      maxHeight:"calc(100dvh - 196px)",
      overflowY:"scroll",
      '&.MuiDialogContent-root': { // Override default padding
        paddingLeft: {
          xs: "0px",
          sm: "16px"
        },
        paddingRight: {
          xs: "0px",
          sm: "16px"
        }
      }
    },
  }

  const newMockAsset = (description: string) => {
    const label = description.substring(4)
    const assetTypeDef = AssetTypeDefs.find((t: AssetTypeDef) => t.label === label)

    const asset = new Asset({
      id: createUUID(),
      accountId: model.id,
      userId: model.userId,
      assetCategory: AssetCategory.LiquidInvestableAssets,
      assetType: assetTypeDef ? assetTypeDef.assetType : AssetType.None,
      description: description,
      ownerId: person ? person.id : null,
      risk: Risk.Moderate,
      balance: 0,
      balanceDate: getISODateToday(),
      start: start,
      end: end ?? null,
      archived: false
    })

    return asset
  }

  useEffect(() => {
    const typedef = getIncomeTypeDef(income.incomeType ?? IncomeType.None)
    setIncomeModel(income)
    setDescription(income.description)
    setAmount(numberToMoneyFormat(income.amount, 0))
    setAmountDate(income.amountDate)
    setIncomeType(income.incomeType)
    if (income.incomeType === IncomeType.WagesSalariesTips) {
      setIsPaycheck(income.isPaycheck && Boolean(income.grossAmount))

      if (!income.createdAt) {
        // Add default incomes, deductions and taxes for Wages, Salaries, and Tips
        setIncomes([
          {id: createUUID(), description: "Gross Wages", amount: "$0", incomeType: IncomeType.WagesSalariesTips, locked: true},
          {id: createUUID(), description: "Other Non-Cash Benefits", amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: true},
        ])
        setDeductions([
          {id: createUUID(), description: "401k (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax, assetRequired: true},
          {id: createUUID(), description: "401k (Employer)", amount: "$0", deductionType: DeductionType.Employer, assetRequired: true},
          // {id: createUUID(), description: "FSA (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax},
          // {id: createUUID(), description: "HSA (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax},
          // {id: createUUID(), description: "HSA (Employer)", amount: "$0", deductionType: DeductionType.Employer},
          {id: createUUID(), description: "Pension (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax, assetRequired: false},
          {id: createUUID(), description: "Pension (Employer)", amount: "$0", deductionType: DeductionType.Employer, assetRequired: false},
          {id: createUUID(), description: "Health Benefits (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax},
          {id: createUUID(), description: "Roth 401k (Employee)", amount: "$0", deductionType: DeductionType.AfterTax, assetRequired: true},
          // {id: createUUID(), description: "Roth 401k (Employer)", amount: "$0", deductionType: DeductionType.Employer},
          {id: createUUID(), description: "Other Before Tax Deductions", amount: "$0", deductionType: DeductionType.BeforeTax},
          {id: createUUID(), description: "Other After Tax Deductions", amount: "$0", deductionType: DeductionType.AfterTax},
        ])
        setTaxes([
          {id: createUUID(), description: "Federal Income Tax", taxType: TaxType.FederalIncomeTax, amount: "$0"},
          {id: createUUID(), description: "State Income Tax", taxType: TaxType.StateIncomeTax, amount: "$0"},
          {id: createUUID(), description: "Local Income Tax", taxType: TaxType.LocalIncomeTax, amount: "$0"},
          {id: createUUID(), description: "Social Security (FICA)", taxType: TaxType.SocialSecurityTax, amount: "$0"},
          {id: createUUID(), description: "Medicare", taxType: TaxType.MedicareTax, amount: "$0"},
        ])
        setIsPaycheck(true)
      } else {
        // Load related incomes, deductions and taxes
        const incs = model.incomes.filter((inc: Income) => inc.paycheckId === income.id)
        const grossIncome = {id: createUUID(), description: "Gross Wages", amount: numberToMoneyFormat(income.grossAmount ?? income.amount, 0), incomeType: IncomeType.WagesSalariesTips, locked: true}
        setIncomes([grossIncome, ...incs.map((inc: Income) => ({id: inc.id, createdAt: inc.createdAt, description: inc.description, amount: numberToMoneyFormat(inc.amount, 0), incomeType: inc.incomeType, imputed: inc.imputed}))])
        const deds = model.deductions.filter((deduction: Deduction) => deduction.paycheckId === income.id)
        setDeductions(deds.map((deduction: Deduction) => {
          const asset = model.getAssetById(deduction.assetId)
          return ({
            id: deduction.id,
            createdAt: deduction.createdAt,
            description: deduction.description,
            amount: numberToMoneyFormat(deduction.amount, 0),
            asset: model.getAssetById(deduction.assetId),
            deductionType: deduction.deductionType,
            assetRequired: Boolean(asset)
          })
        }))
        const txs = model.taxes.filter((tax: Tax) => tax.paycheckId === income.id)
        setTaxes(txs.map((tax: Tax) => ({id: tax.id, createdAt: tax.createdAt, description: tax.description, taxType: tax.taxType, amount: numberToMoneyFormat(tax.amount, 0)})))
        setIsPaycheck(true)
      }
    }
    const person = income.ownerId ? model.getPerson(income.ownerId) : model.getDefaultPerson(typedef.allowJoint)
    setPerson(person)
    setOwner(person ? person.nickname : undefined)
    setAllowJoint(typedef.allowJoint)
    setSchedule(income.schedule ?? new Schedule({name: "Monthly", frequency: Frequency.Monthly, interval: 1}))
    setStart(income.start)
    setEnd(income.end)
    setAnnualInf(isNumber(income.annualInf) ? numberToPercentFormat(income.annualInf, 2) : "")
    if (income.infLock) {
      setAnnualInfLock("User")
    } else if (model.hasLock(income.id, "annualInf")) {
      setAnnualInfLock("Model")
    }
    setSurvivorPercent(isNumber(income.survivorPercent) ? numberToPercentFormat(income.survivorPercent / 100, 2) : undefined)
    setAmountLock(model.hasLock(income.id, "amount"))
    setStartLock(model.hasLock(income.id, "start"))
    setIsLocked(model.isLocked() || model.hasLock(income.id, "*"))
    setArchived(income.archived)
    setNewAssets(listAssetTypeDefs().filter((t: AssetTypeDef) => t.isIRA && !t.requireExtraFields)
      .map((t: AssetTypeDef) => newMockAsset(`New ${t.label}`)))
    setIsOpen(open === true)
  }, [income, open])

  useEffect(() => {
    if (person) {
      setMilestones(getMilestones(person))
    }
  }, [person])

  useEffect(() => {
    const typedef = getIncomeTypeDef(incomeType ?? IncomeType.None)
    setAllowJoint(typedef.allowJoint)
  }, [incomeType])

  useEffect(() => {
    updateSummary()
  }, [income, incomes, deductions, taxes])

  const updateSummary = () => {
    const totalIncomes = incomes.reduce((accum: number, income: IncomeEdit) => {
      return income.incomeType !== IncomeType.None && !income.imputed ? accum + moneyToNumberFormat(income.amount) : accum
    }, 0)
    const totalImputedIncomes = incomes.reduce((accum: number, income: IncomeEdit) => {
      return income.incomeType !== IncomeType.None && income.imputed ? accum + moneyToNumberFormat(income.amount) : accum
    }, 0)
    const totalBeforeTaxDeductions = deductions.filter((deduction: DeductionEdit) => deduction.deductionType === DeductionType.BeforeTax).reduce((accum: number, deduction: DeductionEdit) => accum + moneyToNumberFormat(deduction.amount), 0)
    const totalTaxes = taxes.reduce((accum: number, tax: TaxEdit) => accum + moneyToNumberFormat(tax.amount), 0)
    const totalAfterTaxDeductions = deductions.filter((deduction: DeductionEdit) => deduction.deductionType === DeductionType.AfterTax).reduce((accum: number, deduction: DeductionEdit) => accum + moneyToNumberFormat(deduction.amount), 0)
    const totalEmployerContributions = deductions.filter((deduction: DeductionEdit) => deduction.deductionType === DeductionType.Employer).reduce((accum: number, deduction: DeductionEdit) => accum + moneyToNumberFormat(deduction.amount), 0)

    setIncomesTotal(totalIncomes)
    setImputedIncomesTotal(totalImputedIncomes)
    setBeforeTaxDeductionsTotal(-totalBeforeTaxDeductions)
    setTaxesTotal(-totalTaxes)
    setAfterTaxDeductionsTotal(-totalAfterTaxDeductions)
    setEmployerContributionsTotal(totalEmployerContributions)
    const taxableIncome = totalIncomes /* + totalImputedIncomes */ - totalBeforeTaxDeductions
    setTaxableIncomeTotal(taxableIncome)
    setAmount(numberToMoneyFormat(taxableIncome, 0))
    setNetIncomeTotal(totalIncomes - totalBeforeTaxDeductions - totalTaxes - totalAfterTaxDeductions)
  }

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const getEnd = () => {
    if (end) {
      return end
    } else {
      if (person) {
        if (person === model.person1) {
          return MilestoneId.retirement1
        } else if (person === model.person2) {
          return MilestoneId.retirement2
        } else {
          return MilestoneId.latestRetirement
        }
      }
    }

    return null
  }

  const handleSave = async (event: any) => {
    try {
      let updatedIncome: Income | undefined
      const amountValue = moneyToNumberFormat(amount, 0) ?? 0
      const grossAmount = moneyToNumberFormat(incomes[0].amount, 0)
      const useEnd = getEnd()
      const sortOrder : SortOrder = {
        asset: model.assets.length > 0 ? model.assets[model.assets.length - 1].sortOrder + ModelStore.orderInterval : 0,
        income: model.incomes.length > 0 ? model.incomes[model.incomes.length - 1].sortOrder + ModelStore.orderInterval : 0,
        deduction: model.deductions.length > 0 ? model.deductions[model.deductions.length - 1].sortOrder + ModelStore.orderInterval : 0,
        tax: model.taxes.length > 0 ? model.taxes[model.taxes.length - 1].sortOrder + ModelStore.orderInterval : 0,
      }

      if (!incomeModel.createdAt) {
        const input: CreateIncomeInput = {
          id: incomeModel.id,
          accountId: incomeModel.accountId,
          userId: incomeModel.userId,
          modelId: incomeModel.modelId,
          description: description,
          amount: amountValue,
          amountDate: amountDate ? getISODateFromDate(amountDate) : null,
          incomeType: incomeType,
          schedule: JSON.stringify(schedule),
          start: start,
          end: useEnd,
          annualInf: annualInf !== undefined ? parseFloat(annualInf) / 100.0 : null,
          infLock: annualInfLock === "User",
          survivorPercent: null,
          ownerId: person ? person.id : null,
          sortOrder: sortOrder.income,
          grossAmount: grossAmount
        }
        if (incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable || incomeType === IncomeType.PensionsAndAnnuitiesTaxable) {
          input.survivorPercent = survivorPercent !== undefined ? parseInt(survivorPercent) : null
        }

        updatedIncome = await modelStore.createIncome(input)
        sortOrder.income += ModelStore.orderInterval
        Tracking.event({action: "Income Created"})
      } else {
        const input: UpdateIncomeInput = {
          id: incomeModel.id,
          accountId: incomeModel.accountId,
          userId: incomeModel.userId,
          modelId: incomeModel.modelId,
          description: description,
          amount: amountValue,
          amountDate: snapshot ? getISODateFromDate(snapshot.date) : null,
          incomeType: incomeType,
          schedule: JSON.stringify(schedule),
          start: start,
          end: useEnd,
          annualInf: annualInf !== undefined ? parseFloat(annualInf) / 100.0 : null,
          infLock: annualInfLock === "User",
          survivorPercent: null,
          ownerId: person ? person.id : null,
          grossAmount: grossAmount
        }
        if (amountLock) {
          delete input.amount
          delete input.amountDate
        }
        if (startLock) {
          delete input.start
        }
        if (annualInfLock === "Model") {
          delete input.annualInf
        }

        if (incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable || incomeType === IncomeType.PensionsAndAnnuitiesTaxable) {
          input.survivorPercent = survivorPercent !== undefined ? parseInt(survivorPercent) : null
        }

        updatedIncome = await modelStore.updateIncome(input)
        Tracking.event({action: "Income Updated"})
      }

      if (onSave && updatedIncome) {
        if (incomes.length > 0) {
          await handleSaveIncomes(updatedIncome, sortOrder)
        }
        if (deductions.length > 0) {
          await handleSaveDeductions(updatedIncome, sortOrder)
        }
        if (taxes.length > 0) {
          await handleSaveTaxes(updatedIncome, sortOrder)
        }
        onSave(updatedIncome)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const handleSaveIncomes = async (income: Income, sortOrder: SortOrder) => {
    // Compute any additional incomes for pre-tax deductions and employer contributions
    let totalBeforeTaxDeductions = 0
    deductions.forEach((item: DeductionEdit) => {
      if (item.deductionType === DeductionType.BeforeTax) {
        totalBeforeTaxDeductions += moneyToNumberFormat(item.amount, 0) ?? 0
      }
    })
    const useEnd = getEnd()

    const allIncomes: IncomeEdit[] = [...incomes]
    const beforeTaxIncomeId = `${incomeModel.id}-before-tax`
    const beforeTaxIncome = incomes.find((item: IncomeEdit) => item.incomeType === IncomeType.None && (item.id === beforeTaxIncomeId || item.description === beforeTaxIncomeDesc))
    if (beforeTaxIncome) {
      beforeTaxIncome.amount = numberToMoneyFormat(totalBeforeTaxDeductions, 0)
    } else if (totalBeforeTaxDeductions > 0) {
      allIncomes.push({
        id: beforeTaxIncomeId,
        description: beforeTaxIncomeDesc,
        incomeType: IncomeType.None,
        amount: numberToMoneyFormat(totalBeforeTaxDeductions, 0)
      })
    }

    const promises: Promise<Income | undefined>[] = []
    allIncomes.forEach((item: IncomeEdit) => {
      if (!item.locked) {
        const amountValue = moneyToNumberFormat(item.amount, 0) ?? 0
        if (!item.createdAt && amountValue > 0) {
          const input: CreateIncomeInput = {
            id: item.id,
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            description: item.description,
            amount: amountValue,
            amountDate: amountDate ? getISODateFromDate(amountDate) : null,
            schedule: JSON.stringify(schedule),
            start: start,
            end: useEnd,
            incomeType: item.incomeType,
            ownerId: person ? person.id : null,
            paycheckId: income.id,
            imputed: item.imputed,
            sortOrder: sortOrder.income
          }
          promises.push(modelStore.createIncome(input))
          sortOrder.income += ModelStore.orderInterval
        } else if (item.createdAt && amountValue > 0) {
          const input: UpdateIncomeInput = {
            id: item.id,
            description: item.description,
            amount: amountValue,
            amountDate: amountDate ? getISODateFromDate(amountDate) : null,
            schedule: JSON.stringify(schedule),
            start: start,
            end: useEnd,
            incomeType: item.incomeType,
            ownerId: person ? person.id : null,
            paycheckId: income.id,
            imputed: item.imputed,
          }
          promises.push(modelStore.updateIncome(input))
        } else if (item.createdAt && amountValue <= 0) {
          promises.push(modelStore.deleteIncome(item.id))
        }
      }
    })
    await Promise.all(promises)
  }

  const handleSaveDeductions = async (income: Income, sortOrder: SortOrder) => {
    const useEnd = getEnd()

    const promises: Promise<IModelItem | undefined>[] = []
    const createdAssets: Asset[] = []

    for (let item of deductions) {
      let asset = item.asset
      const employerIncomeId = `${item.id}-income`

      const amountValue = moneyToNumberFormat(item.amount, 0) ?? 0
      if (amountValue > 0 && item.asset && !item.asset.createdAt && item.asset.id !== "none") {
        // Check if asset was already created
        asset = createdAssets.find((a: Asset) => a.id === item.asset!.id)
        if (!asset) {
          // Create new asset
          asset = item.asset
          const input: CreateAssetInput = {
            id: asset.id,
            accountId: model.accountId,
            modelId: model.id,
            userId: model.userId,
            assetCategory: AssetCategory.LiquidInvestableAssets,
            assetType: asset.assetType,
            description: `${person!.nickname}'s ${asset.assetTypeDef.label}`,
            ownerId: person ? person.id : null,
            risk: Risk.Moderate,
            balance: 0,
            balanceDate: getISODateToday(),
            start: start,
            end: useEnd,
            sortOrder: sortOrder.asset
          }
          const createdAsset = await modelStore.createAsset(input)
          if (createdAsset) {
            createdAssets.push(createdAsset)
            asset = createdAsset
          }
          sortOrder.asset += ModelStore.orderInterval
        }
      } else {
        asset = item.asset
      }

      if (!item.createdAt && amountValue > 0) {
        const input: CreateDeductionInput = {
          id: item.id,
          accountId: model.accountId,
          userId: model.userId,
          modelId: model.id,
          description: item.description,
          amount: amountValue,
          amountDate: amountDate ? getISODateFromDate(amountDate) : null,
          schedule: JSON.stringify(schedule),
          start: start,
          end: useEnd,
          assetId: asset ? asset.id : null,
          paycheckId: income.id,
          ownerId: person ? person.id : null,
          deductionType: item.deductionType,
          sortOrder: sortOrder.deduction
        }
        promises.push(modelStore.createDeduction(input))
        sortOrder.deduction += ModelStore.orderInterval

        if (item.deductionType === DeductionType.Employer) {
          // Create an income using the deduction id+inc
          const incomeInput: CreateIncomeInput = {
            id: employerIncomeId,
            accountId: model.accountId,
            userId: model.userId,
            modelId: model.id,
            description: item.description,
            amount: amountValue,
            amountDate: amountDate ? getISODateFromDate(amountDate) : null,
            schedule: JSON.stringify(schedule),
            start: start,
            end: useEnd,
            paycheckId: income.id,
            ownerId: person ? person.id : null,
            incomeType: IncomeType.None,
            sortOrder: sortOrder.income
          }
          promises.push(modelStore.createIncome(incomeInput))
          sortOrder.income += ModelStore.orderInterval
        }
      } else if (item.createdAt && amountValue > 0) {
        const input: UpdateDeductionInput = {
          id: item.id,
          description: item.description,
          amount: amountValue,
          amountDate: amountDate ? getISODateFromDate(amountDate) : null,
          schedule: JSON.stringify(schedule),
          start: start,
          end: useEnd,
          assetId: item.asset ? item.asset.id : null,
          paycheckId: income.id,
          ownerId: person ? person.id : null,
          deductionType: item.deductionType,
        }
        promises.push(modelStore.updateDeduction(input))

        if (item.deductionType === DeductionType.Employer) {
          // Update the income using the same id-income
          const found = model.incomes.find((i: Income) => i.id === employerIncomeId)
          if (found) {
            const incomeInput: UpdateIncomeInput = {
              id: employerIncomeId,
              description: item.description,
              amount: amountValue,
              amountDate: amountDate ? getISODateFromDate(amountDate) : null,
              schedule: JSON.stringify(schedule),
              start: start,
              end: useEnd,
              paycheckId: income.id,
              ownerId: person ? person.id : null,
              incomeType: IncomeType.None,
            }
            promises.push(modelStore.updateIncome(incomeInput))
          }
        }
      } else if (item.createdAt && amountValue <= 0) {
        promises.push(modelStore.deleteDeduction(item.id))
        if (item.deductionType === DeductionType.Employer) {
          promises.push(modelStore.deleteIncome(employerIncomeId))
        }
      }
    }
    await Promise.all(promises)
  }
  
  const handleSaveTaxes = async (income: Income, sortOrder: SortOrder) => {
    const useEnd = getEnd()
    const promises: Promise<Tax | undefined>[] = []
    taxes.forEach((item: TaxEdit) => {
      const amountValue = moneyToNumberFormat(item.amount, 0) ?? 0
      if (!item.createdAt && amountValue > 0) {
        const input: CreateTaxInput = {
          id: item.id,
          accountId: model.accountId,
          userId: model.userId,
          modelId: model.id,
          description: item.description,
          amount: amountValue,
          amountDate: amountDate ? getISODateFromDate(amountDate) : null,
          schedule: JSON.stringify(schedule),
          start: start,
          end: useEnd,
          taxType: item.taxType,
          ownerId: person ? person.id : null,
          paycheckId: income.id,
          sortOrder: sortOrder.tax
        }
        promises.push(modelStore.createTax(input))
        sortOrder.tax += ModelStore.orderInterval
      } else if (item.createdAt && amountValue > 0) {
        const input: UpdateTaxInput = {
          id: item.id,
          description: item.description,
          amount: amountValue,
          amountDate: amountDate ? getISODateFromDate(amountDate) : null,
          schedule: JSON.stringify(schedule),
          start: start,
          end: useEnd,
          taxType: item.taxType,
          ownerId: person ? person.id : null,
          paycheckId: income.id,
        }
        promises.push(modelStore.updateTax(input))
      } else if (item.createdAt && amountValue <= 0) {
        promises.push(modelStore.deleteTax(item.id))
      }
    })
    await Promise.all(promises)
  }

  const handleArchive = async () => {
    if (onArchive) {
      if (incomeModel.createdAt) {
        const archivedValue = !incomeModel.archived
        const income = await modelStore.updateIncome({id: incomeModel.id, archived: archivedValue})
        if (income) {
          // Archive any associated incomes, deductions and taxes
          const incomes = model.incomes.filter((i: Income) => i.paycheckId === incomeModel.id)
          if (incomes.length > 0) {
            const promises: Promise<Income | undefined>[] = []
            incomes.forEach((i: Income) => {
              promises.push(modelStore.updateIncome({id: i.id, archived: archivedValue}))
            })
            await Promise.all(promises)
          }
          const deductions = model.deductions.filter((d: Deduction) => d.paycheckId === incomeModel.id)
          if (deductions.length > 0) {
            const promises: Promise<Deduction | undefined>[] = []
            deductions.forEach((d: Deduction) => {
              promises.push(modelStore.updateDeduction({id: d.id, archived: archivedValue}))
            })
            await Promise.all(promises)
          }
          const taxes = model.taxes.filter((t: Tax) => t.paycheckId === incomeModel.id)
          if (taxes.length > 0) {
            const promises: Promise<Tax | undefined>[] = []
            taxes.forEach((t: Tax) => {
              promises.push(modelStore.updateTax({id: t.id, archived: archivedValue}))
            })
            await Promise.all(promises)
          }
          onArchive(income)
        }
      } else if (onDelete) {
        onDelete(incomeModel)
      }
    }
  }

  const handleDelete = async () => {
    if (onDelete) {
      if (incomeModel.createdAt) {
        const deleted = await modelStore.deleteIncome(incomeModel.id)
        if (deleted) {
          // Delete any associated incomes, deductions and taxes
          const incomes = model.incomes.filter((i: Income) => i.paycheckId === incomeModel.id)
          if (incomes.length > 0) {
            const promises: Promise<Income | undefined>[] = []
            incomes.forEach((i: Income) => {
              promises.push(modelStore.deleteIncome(i.id))
            })
            await Promise.all(promises)
          }
          const deductions = model.deductions.filter((d: Deduction) => d.paycheckId === incomeModel.id)
          if (deductions.length > 0) {
            const promises: Promise<Deduction | undefined>[] = []
            deductions.forEach((d: Deduction) => {
              promises.push(modelStore.deleteDeduction(d.id))
            })
            await Promise.all(promises)
          }
          const taxes = model.taxes.filter((t: Tax) => t.paycheckId === incomeModel.id)
          if (taxes.length > 0) {
            const promises: Promise<Tax | undefined>[] = []
            taxes.forEach((t: Tax) => {
              promises.push(modelStore.deleteTax(t.id))
            })
            await Promise.all(promises)
          }
          onDelete(deleted)
        }
      } else {
        onDelete(incomeModel)
      }
    }
  }

  const getMilestones = (person: Person): Milestone[] => {
    let options: Milestone[] = []
    const timeline = calculator.personTimeline(person)
    if (timeline) {
      const ages = [62, 63, 64, 65, 66, 67, 68, 69, 70]
      options = ages.map((age: number) => {
        let label
        if (age === timeline.earlySocialSecurityAge) {
          label = `${age} (Early Social Security)`
        } else if (age === timeline.fullSocialSecurityAge) {
          label = `${age} (Full Social Security)`
        } else if (age === timeline.delayedSocialSecurityAge) {
          label = `${age} (Delayed Social Security)`
        } else {
          label = String(age)
        }
        const date = addYears(timeline.birthDate, age)
        return new Milestone({
          id: getISODateFromDate(date),
          label: label,
          date: date
        })
      })
    }
    return options
  }

  const handleInfLock = (typename: string) => {
    if (annualInfLock === "User") {
      if (model.getOverrideInflationLock(typename)) {
        setAnnualInfLock("Model")
        setAnnualInf(numberToPercentFormat(model.getDefaultInflationRate(typename)))
      } else {
        setAnnualInfLock(undefined)
      }
    } else if (annualInfLock === "Model") {
      setAnnualInfLock("User")
    } else if (annualInfLock === undefined) {
      setAnnualInfLock("User")
    }
  }

  const getEndHelperText = (incomeType?: IncomeType) => {
    let helperText = ""
    if (incomeType !== undefined) {
      const incomeTypeDef = getIncomeTypeDef(incomeType)
      helperText = (incomeTypeDef.defaultEndDate === 'retireDate') ?
        "Default: Owner's Retirement" : "Default: Owner's Life Exp"
    }
    return helperText
  }

  const renderAnnualInfField = (typename: string) => {
    let endAdornment
    let helperText
    let disabled = false
    if (userStore.isFree) {
      helperText = `Default: ${Model.defaultInflationRate}`
    } else if (annualInfLock === "User") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
          >
            <LockPersonIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by User"
    } else if (annualInfLock === "Model") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
          >
            <LockIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by Inflation Strategy"
      disabled = true
    } else {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
          >
            <LockOpenIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = `Default: ${numberToPercentFormat(model.getDefaultInflationRate(typename), 2)}`
    }

    return (
      <TextFieldValidator
        type="string"
        validators={{ required: false, minValue: 0 }}
        name="annualInf"
        variant="standard"
        margin="dense"
        fullWidth
        label="Annual Inflation %"
        value={annualInf}
        sx={{width:"50%"}}
        InputProps={{
          endAdornment: endAdornment
        }}
        helperText={helperText}
        disabled={isLocked || disabled}
        onChange={(event: any) => setAnnualInf(event.target.value)}
      />
    )
  }

  if (!incomeModel || !isOpen) {
    return null
  }

  // Incomes (Wages and Taxable Benefits

  const setIncomeProperty = (id: string, property: string, value: any) => {
    const index = incomes.findIndex((i: IncomeEdit) => i.id === id)
    if (index >= 0) {
      const newIncomes = [...incomes];
      (newIncomes[index] as any)[property] = value
      setIncomes(newIncomes)
    }
  }

  const addIncome = (index: number, imputed: boolean) => {
    if (imputed) {
      setIncomes([...incomes, {id: createUUID(), description: imputedIncomeOptions[index].description, amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: imputed}])
    } else {
      setIncomes([...incomes, {id: createUUID(), description: incomeOptions[index].description, amount: "$0", incomeType: IncomeType.WagesSalariesTips, imputed: imputed}])
    }
  }

  const removeIncome = (id: string) => {
    const index = incomes.findIndex((i: IncomeEdit) => i.id === id)
    if (index >= 0) {
      const income = incomes[index]
      if (income.createdAt) {
        setIncomeProperty(income.id, "amount", "$0")
      } else {
        const newIncomes = [...incomes]
        newIncomes.splice(index, 1)
        setIncomes(newIncomes)
      }
    }
  }

  const getIncomeMenuOptions = (imputed: boolean) => {
    if (imputed) {
      return imputedIncomeOptions.map((item: IncomeEdit) => item.description)
    } else {
      return incomeOptions.map((item: IncomeEdit) => item.description)
    }
  }
  
  const renderIncomes = (imputed: boolean) => {
    let title = ""
    let instructions = ""
    if (imputed) {
      title = "Imputed Income"
      instructions = "Enter your non-cash benefits that must be treated as taxable wages (e.g., Employer provided life Insurance, Personal Use of Company Car, etc.)"
    } else {
      title = "Wages"
      instructions = "Enter your taxable wages (e.g., Wages, Salary, Tips, etc.)"
    }
    return (
      <Paper elevation={1} sx={{mt:0, pl:2, py:1}}>
        <Grid container spacing={1} direction="column">
          <Grid item xs={12}>
            <Typography variant="h5" color="primary" gutterBottom>{title}</Typography>
            <Typography variant="body2" sx={styles.instructions}>{instructions}</Typography>
          </Grid>
          {incomes.filter((item: IncomeEdit) => item.incomeType !== IncomeType.None && Boolean(item.imputed) === imputed)
            .map((item: IncomeEdit, index: number) =>
            <Grid item xs={12} key={item.id}>
              <Grid container spacing={2} width="100%" direction="row" alignItems="center">
                <Grid item xs={7}>
                  <TextFieldValidator
                    margin="dense"
                    name="description"
                    label="Description"
                    type="text"
                    fullWidth
                    variant="standard"
                    required
                    validators={{required: true}}
                    value={item.description}
                    disabled={isLocked || item.locked}
                    onChange={(event: any) => setIncomeProperty(item.id, 'description', event.target.value)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextFieldValidator
                    type="string"
                    validators={{ isMoney: true }}
                    name="amount"
                    variant="standard"
                    margin="dense"
                    label="Amount"
                    value={item.amount}
                    disabled={isLocked}
                    onChange={(event: any) => setIncomeProperty(item.id, 'amount', event.target.value)}
                    InputLabelProps={{shrink: true}}
                  />
                </Grid>
                <Grid item xs={1} sx={{paddingTop:"32px !important"}}>
                  {!isLocked && !item.locked &&
                    <IconButton size="small" onClick={() => removeIncome(item.id)}>
                      <DeleteIcon/>
                    </IconButton>
                  }
                </Grid>
              </Grid>
            </Grid>
          )}
          {!isLocked &&
            <Grid item xs={12} sx={{display:"flex", justifyContent:"flex-start"}}>
              <BasicMenuButton
                icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
                label={`Add ${title}`} menuPosition="right"
                options={getIncomeMenuOptions(imputed)}
                onClickOption={(index: number) =>addIncome(index, imputed)}
              />
            </Grid>
          }
        </Grid>
      </Paper>
    )
  }

  // Deductions

  const deductionOptions = [
    {id: "", description: "401k (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax, assetRequired: true},
    {id: "", description: "401k (Employer)", amount: "$0", deductionType: DeductionType.Employer, assetRequired: true},
    {id: "", description: "FSA (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax, assetRequired: true},
    {id: "", description: "HSA (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax, assetRequired: true},
    {id: "", description: "HSA (Employer)", amount: "$0", deductionType: DeductionType.Employer, assetRequired: true},
    {id: "", description: "Pension (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax, assetRequired: false},
    {id: "", description: "Pension (Employer)", amount: "$0", deductionType: DeductionType.Employer, assetRequired: false},
    {id: "", description: "Health Benefits (Employee)", amount: "$0", deductionType: DeductionType.BeforeTax},
    {id: "", description: "Roth 401k (Employee)", amount: "$0", deductionType: DeductionType.AfterTax, assetRequired: true},
    {id: "", description: "Roth 401k (Employer)", amount: "$0", deductionType: DeductionType.Employer, assetRequired: true},
    {id: "", description: "Other Before Tax Deductions", amount: "$0", deductionType: DeductionType.BeforeTax},
    {id: "", description: "Other After Tax Deductions", amount: "$0", deductionType: DeductionType.AfterTax},
  ]

  const getDeductionOptions = (deductionType: DeductionType) => {
    const options = deductionOptions.filter((d: DeductionEdit) => d.deductionType === deductionType)
    return options
  }

  const getDeductionMenuOptions = (deductionType: DeductionType) => {
    const options = deductionOptions.filter((d: DeductionEdit) => d.deductionType === deductionType).map((d: DeductionEdit) => d.description)
    return options
  }

  const setDeductionProperty = (id: string, property: string, value: any) => {
    const newDeductions = [...deductions]
    const index = newDeductions.findIndex((d: DeductionEdit) => d.id === id)
    if (index >= 0) {
      (newDeductions[index] as any)[property] = value
      setDeductions(newDeductions)
    }
  }

  const addDeduction = (index: number, deductionType: DeductionType) => {
    const deductionOptions = getDeductionOptions(deductionType)
    const option = deductionOptions[index]
    if (option) {
      setDeductions([...deductions, {id: createUUID(), description: option.description, amount: option.amount, deductionType: option.deductionType, assetRequired: option.assetRequired}])
    }
  }

  const removeDeduction = (id: string) => {
    const index = deductions.findIndex((d: DeductionEdit) => d.id === id)
    if (index >= 0) {
      const deduction = deductions[index]
      if (deduction.createdAt) {
        setDeductionProperty(id, "amount", "$0")
      } else {
        const newDeductions = [...deductions]
        newDeductions.splice(index, 1)
        setDeductions(newDeductions)
      }
    }
  }

  const renderDeductions = (deductionType: DeductionType) => {
    // Add New asset options
    let title = ""
    let instructions : string | ReactNode = ""
    let addLabel = "Add Deduction"
    if (deductionType === DeductionType.BeforeTax) {
      title = "Before Tax Deductions"
      instructions = <span>Enter non-taxable, employee contributions to retirement, FSA, and HSA accounts. Select existing accounts or add new ones for these. <br/>
        Also enter other non-taxable, employee paid benefits for items such as medical, dental, and vision plans.</span>
      addLabel = "Add Before Tax Deduction"

    } else if (deductionType === DeductionType.AfterTax) {
      title = "After Tax Deductions"
      instructions = "Enter taxable contributions to Roth retirement accounts and any other after tax deductions."
      addLabel = "Add After Tax Deduction"
    } else if (deductionType === DeductionType.Employer) {
      title = "Employer Contributions"
      instructions = "Enter employer contributions to retirement plans. Select existing accounts or add new ones."
      addLabel = "Add Employer Contribution"
    }

    return (
      <Paper elevation={1} sx={{mt:1, pl:2, py:1}}>
        <Grid container spacing={1} direction="column">
          <Grid item xs={12}>
            <Typography variant="h5" color="primary" gutterBottom>{title}</Typography>
            <Typography variant="body2" sx={styles.instructions}>{instructions}</Typography>
          </Grid>
          {deductions.filter((item: DeductionEdit) => item.deductionType === deductionType)
                     .map((item: DeductionEdit, index: number) =>
            <Grid item xs={12} key={item.id}>
              <Grid container spacing={2} width="100%" direction="row" alignItems="center">
                <Grid item xs={item.assetRequired ? 4 : 7}>
                  <TextFieldValidator
                    margin="dense"
                    name="description"
                    label="Description"
                    type="text"
                    fullWidth
                    variant="standard"
                    required
                    validators={{required: true}}
                    value={item.description}
                    disabled={isLocked}
                    onChange={(event: any) => setDeductionProperty(item.id, 'description', event.target.value)}
                  />
                </Grid>
                <Grid item xs={item.assetRequired ? 3 : 4}>
                  <TextFieldValidator
                    type="string"
                    validators={{ isMoney: true }}
                    name="amount"
                    variant="standard"
                    margin="dense"
                    fullWidth
                    label="Amount"
                    value={item.amount}
                    disabled={isLocked}
                    onChange={(event: any) => setDeductionProperty(item.id, 'amount', event.target.value)}
                    InputLabelProps={{shrink: true}}
                  />
                </Grid>
                {item.assetRequired &&
                  <Grid item xs={4}>
                    <TextFieldValidator
                      type="text"
                      validators={{ required: item.assetRequired && moneyToNumberFormat(item.amount) > 0 }}
                      // required
                      name="asset"
                      label="Account"
                      fullWidth
                      variant="standard"
                      margin="dense"
                      autocompleteOptions={{
                        freeSolo: false,
                        options: [...model.getAssetsByCategory(AssetCategory.LiquidInvestableAssets), ...newAssets],
                        getOptionLabel: (option: Asset) => option ? option.description : "None",
                        isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
                        value: item.asset,
                        disabled: isLocked,
                        onChange: (event: any, value: Asset, reason: any) => {
                          if (value.id === "new") {
                            // TODO: addDeductionAsset()
                          } else {
                            setDeductionProperty(item.id, 'asset', value)
                          }
                        }
                      }}
                    />
                  </Grid>
                }
                {!isLocked &&
                  <Grid item xs={1} sx={{paddingTop:"32px !important"}}>
                    <IconButton size="small" onClick={() => removeDeduction(item.id)}>
                      <DeleteIcon/>
                    </IconButton>
                  </Grid>
                }
              </Grid>
            </Grid>
          )}
          {!isLocked &&
            <Grid item xs={12} sx={{display:"flex", justifyContent:"flex-start"}}>
              <BasicMenuButton
                icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
                label={addLabel} menuPosition="right"
                options={getDeductionMenuOptions(deductionType)}
                onClickOption={(index: number) =>addDeduction(index, deductionType)}
              />
            </Grid>
          }
        </Grid>
      </Paper>
    )
  }
  
  // Taxes

  const setTaxProperty = (index: number, property: string, value: any) => {
    const newTaxes = [...taxes];
    (newTaxes[index] as any)[property] = value
    setTaxes(newTaxes)
  }

  const addTax = (index: number) => {
    const taxOption = taxMenuOptions[index]
    if (taxOption) {
      setTaxes([...taxes, {id: createUUID(), description: taxOption.description, amount: "$0", taxType: taxOption.taxType}])
    }
  }

  const removeTax = (index: number) => {
    const tax = taxes[index]
    if (tax.createdAt) {
      setTaxProperty(index, "amount", "$0")
    } else {
      const newTaxes = [...taxes]
      newTaxes.splice(index, 1)
      setTaxes(newTaxes)
    }
  }

  const getTaxMenuOptions = () => {
    return taxMenuOptions.map((item: Tax) => item.description)
  }

  const renderTaxes = () => {
    return (
      <Paper elevation={1} sx={{mt:1, pl:2, py:1}}>
        <Grid container spacing={1} direction="column">
          <Grid item xs={12}>
            <Typography variant="h5" color="primary" gutterBottom>Taxes</Typography>
            <Typography variant="body2" sx={styles.instructions}>Enter taxes withheld from this paycheck.</Typography>
          </Grid>
          {taxes.map((item: TaxEdit, index: number) =>
            <Grid item xs={12} key={item.id}>
              <Grid container spacing={2} width="100%" direction="row" alignItems="center">
                <Grid item xs={4}>
                  <TextFieldValidator
                    margin="dense"
                    name="description"
                    label="Description"
                    type="text"
                    fullWidth
                    variant="standard"
                    required
                    validators={{required: true}}
                    value={item.description}
                    disabled={isLocked}
                    onChange={(event: any) => setTaxProperty(index, 'description', event.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextFieldValidator
                    type="string"
                    validators={{ isMoney: true }}
                    name="amount"
                    variant="standard"
                    margin="dense"
                    fullWidth
                    label="Amount"
                    value={item.amount}
                    disabled={isLocked}
                    onChange={(event: any) => setTaxProperty(index, 'amount', event.target.value)}
                    InputLabelProps={{shrink: true}}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextFieldValidator
                    type="text"
                    validators={{ required: true }}
                    required
                    name="taxType"
                    label="Tax Type"
                    variant="standard"
                    margin="dense"
                    autocompleteOptions={{
                      freeSolo: false,
                      options: Object.values(TaxType).filter((v) => !isNaN(Number(v))),
                      getOptionLabel: (option: number) => {
                        return(humanizeString(TaxType[option]))
                      },
                      value: item.taxType,
                      disabled: isLocked,
                      onChange: (event: any, value: number, reason: any) => {
                        setTaxProperty(index, 'taxType', value)
                      }
                    }}
                  />
                </Grid>
                {!isLocked &&
                  <Grid item xs={1} sx={{paddingTop:"32px !important"}}>
                    <IconButton size="small" onClick={() => removeTax(index)}>
                      <DeleteIcon/>
                    </IconButton>
                  </Grid>
                }
              </Grid>
            </Grid>
          )}
          {!isLocked &&
            <Grid item xs={12} sx={{display:"flex", justifyContent:"flex-start"}}>
              <BasicMenuButton
                icon={<AddCircleRounded sx={{color: theme.palette.secondary.light}}/>}
                label="Add Tax Withholding" menuPosition="right"
                options={getTaxMenuOptions()}
                onClickOption={(index: number) =>addTax(index)}
              />
            </Grid>
          }
        </Grid>
      </Paper>
    )
  }

  const renderSummary = () => {
    return (
      <Paper elevation={1} sx={{mt:2,my:0, px:2, py:2}}>
        <Grid container spacing={1} direction="column">
          <Grid container direction="row">
            <Grid item xs={12} pl={1}>
              <Typography variant="h5" color="primary">Paycheck Summary</Typography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={8} sx={styles.summaryCell}>
                <Typography variant="body2">Wages</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.summaryCell}>
                <Typography variant="body2" sx={{textAlign:"right"}}>{numberToMoneyFormat(incomesTotal)}</Typography>
              </Grid>

              <Grid item xs={8} sx={styles.summaryCell}>
                <Typography variant="body2">Imputed Income</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.summaryCell}>
                <Typography variant="body2" sx={{textAlign:"right"}}>{numberToMoneyFormat(imputedIncomesTotal)}</Typography>
              </Grid>

              <Grid item xs={8} sx={styles.summaryCell}>
                <Typography variant="body2">Before Tax Deductions</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.summaryCell}>
                <Typography variant="body2" sx={{textAlign:"right"}}>{numberToMoneyFormat(beforeTaxDeductionsTotal)}</Typography>
              </Grid>

              <Grid item xs={8} sx={styles.summaryCell}>
                <Typography variant="body2">Taxable Income</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.summaryCell}>
                <Typography variant="body2" sx={{textAlign:"right"}}>{numberToMoneyFormat(taxableIncomeTotal)}</Typography>
              </Grid>

              <Grid item xs={8} sx={styles.summaryCell}>
                <Typography variant="body2">Taxes</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.summaryCell}>
                <Typography variant="body2" sx={{textAlign:"right"}}>{numberToMoneyFormat(taxesTotal)}</Typography>
              </Grid>

              <Grid item xs={8} sx={styles.netCell}>
                <Typography variant="body2">After Tax Deductions</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.netCell}>
                <Typography variant="body2" sx={{textAlign:"right"}}>{numberToMoneyFormat(afterTaxDeductionsTotal)}</Typography>
              </Grid>

              <Grid item xs={8} sx={styles.summaryCell}>
                <Typography variant="body2"><b>Net Income</b> (paycheck amount)</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.summaryCell}>
                <Typography variant="body2" sx={{textAlign:"right", fontWeight:700}}>{numberToMoneyFormat(netIncomeTotal)}</Typography>
              </Grid>

              <Grid item xs={8} sx={styles.summaryCell}>
                <Typography variant="body2">Employer Contributions</Typography>
              </Grid>
              <Grid item xs={4} sx={styles.summaryCell}>
                <Typography variant="body2" sx={{textAlign:"right"}}>{numberToMoneyFormat(employerContributionsTotal)}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  const renderLock = () => {
    if (isLocked) {
      let lockMsg = ""
      if (model.isLocked()) {
        lockMsg = model.getLock(model.id, "*") ?? ""
      }
      return (<Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
        <LockIcon sx={{color: theme.palette.primary.main, mr:1}}/>
        <Typography variant="body2" color={theme.palette.primary.main}>
          {lockMsg}
        </Typography>
      </Box>)
    } else {
      return null
    }
  }


  if (quickForm) {
    return (
      <FormValidator id="income-quick-form" onSubmit={handleSave}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{m:0, mb:0, px:2, py:1}}>
            <TextFieldValidator
              margin="normal"
              name="description"
              label="Title"
              type="text"
              fullWidth
              variant="standard"
              required
              autoFocus
              validators={{required: true}}
              value={description}
              disabled={isLocked}
              onChange={(event: any) => setDescription(event.target.value)}
            />
            <TextFieldValidator
              type="text"
              validators={{required: true}}
              required
              name="owner"
              label="Owner"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: model.getPersonNicknames(allowJoint),
                value: owner,
                disabled: isLocked,
                onChange: (event: any, value: string, reason: any) => {
                  setOwner(value)
                  setPerson(model.getPersonByNickname(value))
                }
              }}
            />
            <ScheduleSelector
              value={schedule!}
              disabled={isLocked}
              startDate={model.getDate(start)}
              onChange={(value: Schedule) => setSchedule(value)}
            />
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <MilestoneDateFieldValidator
                name="start"
                label="Start Date"
                model={model}
                value={start}
                width="50%"
                required={incomeType !== IncomeType.SocialSecurityBenefits}
                disabled={isLocked || startLock}
                helperText={startLock ? "Locked by Social Security Strategy" : undefined}
                onChange={(value: string) => setStart(value)}
              />
              <FormGroupSpacer/>
              <MilestoneDateFieldValidator
                name="end"
                label="End Date"
                model={model}
                value={end}
                disabled={isLocked}
                width="50%"
                helperText={getEndHelperText(incomeType)}
                onChange={(value: string) => setEnd(value)}
              />
            </Box>
            <TextFieldValidator
              type="string"
              validators={{required: false, minValue: 0}}
              name="annualInf"
              variant="standard"
              margin="dense"
              sx={{display: "flex", flexGrow: 1}}
              fullWidth
              label="Annual Inflation %"
              helperText={`Default: ${numberToPercentFormat(model.getDefaultInflationRate("income"), 2)}`}
              value={annualInf}
              onChange={(event: any) => setAnnualInf(event.target.value)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {renderIncomes(false)}
          {renderIncomes(true)}
          {renderDeductions(DeductionType.BeforeTax)}
          {renderTaxes()}
          {renderDeductions(DeductionType.AfterTax)}
          {renderDeductions(DeductionType.Employer)}
          <Box height="8px"></Box>
          {renderSummary()}
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <DialogButton variant="primary" type="submit" spacing="right" data-testid="paycheck-quick-save">
                Save
              </DialogButton>
              <DialogButton variant="secondary" type="button" onClick={handleClose} spacing="right" data-testid="paycheck-quick-cancel">
                Cancel
              </DialogButton>
            </Box>
          </Box>
        </Grid>
      </Grid>
      </FormValidator>
    )
  }

  return (
    <ModelEditDialog title="Edit Paycheck" open={isOpen} size={isMobile ? "xs" : "md"} sxContent={styles.dialogContent}
                     onCancel={handleClose}
                     onSave={!isLocked ? handleSave : undefined}
                     onDelete={!isLocked ? handleDelete : undefined}
                     archived={archived}
                     onArchive={!isLocked && onArchive ? handleArchive : undefined}
    >

      <Grid container spacing={1}>
        <Grid item xs={12} sx={{px:0, mx:0}}>
          <Paper elevation={1} sx={{m:0, mb:1, px:2, py:1}}>
            <TextFieldValidator
              margin="normal"
              name="description"
              label="Title"
              type="text"
              fullWidth
              variant="standard"
              required
              validators={{ required: true }}
              value={description}
              disabled={isLocked}
              onChange={(event: any) => setDescription(event.target.value)}
            />
            {!isPaycheck &&
              <Box display="flex" flexDirection="row" flexGrow={1} justifyContent="stretch">
                <TextFieldValidator
                  type="string"
                  validators={{ isMoney: true }}
                  name="amount"
                  variant="standard"
                  margin="dense"
                  sx={{display: "flex", flexGrow: 1}}
                  fullWidth
                  label={incomeType === IncomeType.WagesSalariesTips ? "Taxable Income Amount" : "Amount"}
                  value={amount}
                  disabled={isPaycheck}
                  locked={isLocked || amountLock}
                  helperText={amountLock ? "Locked by Social Security Strategy" : (incomeType === IncomeType.WagesSalariesTips ? "The Taxable Income will be calculated" : undefined)}
                  onChange={(event: any) => setAmount(event.target.value)}
                />
                <FormGroupSpacer/>
                <Box pt={1}>
                  <DatePickerValidator
                    name="amountDate"
                    label="Amount Date"
                    value={amountDate}
                    format="MM-dd-yyyy"
                    variant="standard"
                    margin="dense"
                    sx={{display: "flex", flexGrow: 1}}
                    fullWidth
                    required
                    disabled
                    onChange={(newValue: any) => {
                      setAmountDate(newValue)
                    }}
                  />
                </Box>
              </Box>
            }
            <ScheduleSelector
              value={schedule!}
              disabled={isLocked}
              startDate={model.getDate(start)}
              onChange={(value: Schedule) => setSchedule(value)}
            />
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="owner"
              label="Owner"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: model.getPersonNicknames(allowJoint),
                value: owner,
                disabled: isLocked,
                onChange: (event: any, value: string, reason: any) => {
                  setOwner(value)
                  setPerson(model.getPersonByNickname(value))
                }
              }}
            />
            {!isPaycheck &&
              <TextFieldValidator
                type="text"
                validators={{ required: true }}
                required
                name="incomeType"
                label="Income Type"
                variant="standard"
                autocompleteOptions={{
                  freeSolo: false,
                  options: Object.values(IncomeType).filter((v) => !isNaN(Number(v))),
                  getOptionLabel: (option: number) => {
                    const incomeTypeDef = getIncomeTypeDef(option)
                    return(incomeTypeDef.label)
                  },
                  value: incomeType,
                  disabled: isLocked,
                  onChange: (event: any, value: number, reason: any) => {
                    setIncomeType(value)
                  }
                }}
              />
            }
            {(incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable || incomeType === IncomeType.PensionsAndAnnuitiesTaxable) &&
              <TextFieldValidator
                type="string"
                validators={{ required: false, minValue: 0 }}
                name="survivorPercent"
                variant="standard"
                margin="dense"
                fullWidth
                label="Survivor %"
                value={survivorPercent}
                disabled={isLocked}
                onChange={(event: any) => setSurvivorPercent(event.target.value)}
              />
            }
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              {incomeType === IncomeType.SocialSecurityBenefits && owner &&
                <MilestoneDateFieldValidator
                  name="start"
                  label="Start Date"
                  milestones={milestones}
                  value={start}
                  width="50%"
                  required={incomeType === IncomeType.SocialSecurityBenefits}
                  disabled={isLocked || startLock}
                  helperText={startLock ? "Locked by Social Security Strategy" : undefined}
                  onChange={(value: string) => {
                    setStart(value)
                  }}
                />
              }
              {(incomeType !== IncomeType.SocialSecurityBenefits || !owner) &&
                <MilestoneDateFieldValidator
                  name="start"
                  label="Start Date"
                  model={model}
                  value={start}
                  width="50%"
                  required={incomeType !== IncomeType.SocialSecurityBenefits}
                  disabled={isLocked || startLock}
                  helperText={startLock ? "Locked by Social Security Strategy" : undefined}
                  onChange={(value: string) => setStart(value)}
                />
              }
              <FormGroupSpacer/>
              <MilestoneDateFieldValidator
                name="end"
                label="End Date"
                model={model}
                value={end}
                disabled={isLocked}
                width="50%"
                helperText={getEndHelperText(incomeType)}
                onChange={(value: string) => setEnd(value)}
              />
            </Box>
            {renderAnnualInfField("income")}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {renderIncomes(false)}
          {renderIncomes(true)}
          {renderDeductions(DeductionType.BeforeTax)}
          {renderTaxes()}
          {renderDeductions(DeductionType.AfterTax)}
          {renderDeductions(DeductionType.Employer)}
        </Grid>
        <Grid item xs={12}>
          {renderSummary()}
        </Grid>
      </Grid>
    </ModelEditDialog>
  )
}

export default PaycheckEditDialog